import React from 'react';
import { BarChart3, CalendarRange, ClipboardList, LineChart, SlidersHorizontal } from 'lucide-react';

/**
 * Analytics Setup wizard + General Analytics results preview for the tutorial.
 */
export default function TutorialAnalyticsMockup() {
  const statCards = [
    { label: 'Total calls', value: '248', highlight: true },
    { label: 'Primary outcome', value: '68%', highlight: false },
    { label: 'Secondary metric', value: '42%', highlight: false },
    { label: 'Follow-up rate', value: '19%', highlight: false },
  ];

  return (
    <div className="mt-5" aria-hidden>
      <p className="text-sm font-semibold text-[#459EA3] uppercase tracking-wide mb-2">Measure & review</p>
      <p className="text-sm text-gray-500 mb-4 max-w-4xl">
        <strong className="text-gray-700">Analytics Setup</strong> (Insights) walks you through script, analysis type, prompt, and survey questions.{' '}
        <strong className="text-gray-700">General Analytics</strong> runs batch analysis on Logs for a date range and shows summary cards, breakdowns, and CSV export.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6 min-h-[min(48vh,420px)]">
        {/* Analytics Setup */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden flex flex-col min-h-[min(44vh,380px)]">
          <div className="shrink-0 px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100 bg-gray-50/90">
            <div className="flex items-start gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#459EA3] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Analytics Setup</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Configure analysis type and prompts per script.
                </p>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-b border-gray-200 px-3 pt-2 flex gap-1">
            <span className="rounded-t-lg border border-b-0 border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900">
              Setup Wizard
            </span>
            <span className="rounded-t-lg border border-transparent px-3 py-1.5 text-xs font-semibold text-gray-400">
              Advanced setup
            </span>
          </div>

          <div className="p-4 sm:p-5 space-y-4 flex-1 flex flex-col min-h-0">
            <div className="flex flex-wrap items-center gap-2">
              {[
                { n: 1, label: 'Script & type', active: true },
                { n: 2, label: 'Prompt & questions', active: false },
                { n: 3, label: 'Run & results', active: false },
              ].map((s, i) => (
                <React.Fragment key={s.n}>
                  <div
                    className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                      s.active
                        ? 'border-[#459EA3] bg-[#DBF4F3] text-[#2d7a7e]'
                        : 'border-gray-200 bg-gray-50 text-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${
                        s.active ? 'bg-[#459EA3] text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {s.n}
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold leading-tight max-w-[7rem] sm:max-w-none">
                      {s.label}
                    </span>
                  </div>
                  {i < 2 && <span className="hidden sm:block h-0.5 w-4 bg-gray-200 shrink-0" aria-hidden />}
                </React.Fragment>
              ))}
            </div>

            <div className="flex-1 min-h-0 rounded-xl border border-gray-200 bg-white p-3 sm:p-4 space-y-3 shadow-inner">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Script</span>
                  <div className="mt-1 h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 flex items-center text-xs text-gray-700">
                    My outreach script
                  </div>
                </div>
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Main analysis type</span>
                  <div className="mt-1 h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 flex items-center text-xs text-gray-700">
                    Survey
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardList className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-800">Analysis prompt</span>
                </div>
                <div className="rounded-lg border border-dashed border-[#459EA3]/35 bg-[#f8fffe] p-3 space-y-1.5 min-h-[52px]">
                  <div className="h-2 bg-gray-200/80 rounded w-[92%]" />
                  <div className="h-2 bg-gray-200/60 rounded w-[78%]" />
                  <div className="h-2 bg-gray-200/50 rounded w-[65%]" />
                </div>
              </div>
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Survey questions</span>
                <ul className="mt-2 space-y-1.5 text-[11px] sm:text-xs text-gray-600 font-mono bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                  <li>Q1. How satisfied were you? (1–5)</li>
                  <li>Q2. Would you recommend us?</li>
                  <li>Q3. What could we improve?</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed shrink-0">
              Save as you go — Log transcripts are analyzed using this configuration when you run General Analytics.
            </p>
          </div>
        </div>

        {/* General Analytics results */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden flex flex-col min-h-[min(44vh,380px)]">
          <div className="shrink-0 px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100 bg-gray-50/90">
            <div className="flex items-start gap-2">
              <LineChart className="w-5 h-5 text-[#459EA3] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">General Analytics</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Pick a script, date range, then generate metrics from Logs.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-5 space-y-4 flex-1 flex flex-col min-h-0">
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <div className="flex-1 min-w-0 h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 flex items-center text-xs text-gray-700">
                My outreach script
              </div>
              <div className="flex items-center gap-2 h-10 rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-600 shrink-0">
                <CalendarRange className="w-4 h-4 text-gray-400 shrink-0" />
                Last 30 days
              </div>
              <span className="inline-flex items-center justify-center rounded-lg bg-[#459EA3] px-4 py-2 text-sm font-semibold text-white shadow-sm shrink-0 pointer-events-none">
                Generate
              </span>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3 shrink-0">
              {statCards.map((c) => (
                <div
                  key={c.label}
                  className={`rounded-lg border px-3 py-2.5 min-w-[calc(50%-0.25rem)] sm:min-w-[140px] flex-1 sm:flex-none ${
                    c.highlight
                      ? 'bg-cyan-50 border-cyan-200'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div
                    className={`text-[10px] sm:text-xs font-semibold ${
                      c.highlight ? 'text-sky-700' : 'text-violet-800'
                    }`}
                  >
                    {c.label}
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{c.value}</div>
                </div>
              ))}
            </div>

            <div className="flex-1 min-h-[120px] rounded-xl border border-gray-200 bg-slate-50/80 p-3 sm:p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700">Sample breakdown</span>
              </div>
              <div className="flex-1 flex items-end gap-3 sm:gap-4 min-h-[80px] px-1">
                {[
                  { h: '72%', label: 'Q1' },
                  { h: '45%', label: 'Q2' },
                  { h: '58%', label: 'Q3' },
                ].map((b) => (
                  <div key={b.label} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                    <div
                      className="w-full max-w-[3rem] rounded-t-md bg-gradient-to-t from-[#459EA3] to-[#6AD3CD]"
                      style={{ height: b.h }}
                    />
                    <span className="text-[10px] font-medium text-slate-500">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed shrink-0">
              Open the full report to drill into calls, save snapshots, and <strong className="text-gray-700">download CSV</strong> for spreadsheets.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
